import { useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../../../hooks/AuthContext';
import { useControllerFlowStepsContext } from '../../../hooks/ControllerFlowStepsContext';
import Button from '../../../components/Button/Button';
import ButtonLabel from '../../../components/Button/ButtonLabel';
import Modal, { ModalHandles } from '../../../components/Modal/Modal';
import ModalTitle from '../../../components/Modal/ModalTitle';
import ModalDescription from '../../../components/Modal/ModalDescription';
import Icon from '../../../components/Icon/Icon';
import Loading from '../../../components/Loading/Loading';
import {
  getListAllSessions,
  removeSessions
} from '../../../services/user/session';
import { IUserSession } from '../../../interfaces/user/Session';
import { formatDateToView } from '../../../utils/date';
import {
  convertUserAgentToBrowser,
  convertUserAgentToOS
} from '../../../utils/device';

export default function AtividadesTab() {
  const { checkAuth } = useAuthContext();
  const { setAlertBox } = useControllerFlowStepsContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [currentSession, setCurrentSession] = useState<
    IUserSession | undefined
  >();
  const [sessions, setSessions] = useState<IUserSession[]>([]);
  const [loading, setLoading] = useState(false);
  const modalRef = useRef<ModalHandles>(null);

  useEffect(() => {
    loadSessions();
  }, []);

  async function loadSessions() {
    const check = checkAuth();
    if (!check?.id) return;
    setLoading(true);
    try {
      const response = await getListAllSessions();
      setCurrentSession(
        response?.sessions?.find((s: IUserSession) => s.id === check.sessionId)
      );
      setSessions(response?.sessions || []);
    } catch {
      setAlertBox({
        message:
          'Não foi possível carregar as sessões. Tente novamente mais tarde.',
        error: true
      });
    } finally {
      setLoading(false);
    }
  }

  async function removeSession(sessionId: number) {
    setLoading(true);
    try {
      await removeSessions([sessionId]);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      setAlertBox({ message: 'O dispositivo foi desconectado', error: false });
    } catch {
      setAlertBox({
        message:
          'Não foi possível desconectar o dispositivo. Tente novamente mais tarde.',
        error: true
      });
    } finally {
      setLoading(false);
    }
  }

  async function removeAllSessions() {
    if (sessions.length <= 1) {
      setAlertBox({
        message: 'Não é possível desconectar a própria sessão.',
        error: true
      });
      modalRef.current?.closeModal();
      return;
    }
    try {
      const ids = sessions
        .filter((s) => s.id !== currentSession?.id)
        .map((s) => s.id);
      await removeSessions(ids);
      setSessions(currentSession ? [currentSession] : []);
      setAlertBox({
        message: 'Todos os dispositivos foram desconectados',
        error: false
      });
    } catch {
      setAlertBox({
        message:
          'Não foi possível desconectar os dispositivos. Tente novamente mais tarde.',
        error: true
      });
    } finally {
      modalRef.current?.closeModal();
    }
  }

  function toggleMenu() {
    setIsMenuOpen((prev) => !prev);
  }

  if (loading) {
    return (
      <div className="user-profile__content">
        <div className="user-profile__left-rail">
          <div
            className={`user-profile__left-rail-header${isMenuOpen ? ' user-profile__left-rail-header--open' : ''}`}
            onClick={toggleMenu}
          >
            <span>Atividades</span>
          </div>
          <div className="user-profile__left-rail-divider" />
          <div
            className={`user-profile__left-rail-nav${isMenuOpen ? ' user-profile__left-rail-nav--open' : ''}`}
          >
            <button className="user-profile__nav-item user-profile__nav-item--active">
              Dispositivos conectados
            </button>
          </div>
        </div>

        <div className="user-profile__main">
          <div className="user-profile__main-header">
            <span className="user-profile__main-title">
              Dispositivos conectados
            </span>
          </div>
          <div className="user-profile__main-divider" />
          <div className="up-prefs-panel up-prefs-panel--loading">
            <Loading />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile__content">
      <div className="user-profile__left-rail">
        <div
          className={`user-profile__left-rail-header${isMenuOpen ? ' user-profile__left-rail-header--open' : ''}`}
          onClick={toggleMenu}
        >
          <span>Atividades</span>
        </div>
        <div className="user-profile__left-rail-divider" />
        <div
          className={`user-profile__left-rail-nav${isMenuOpen ? ' user-profile__left-rail-nav--open' : ''}`}
        >
          <button className="user-profile__nav-item user-profile__nav-item--active">
            Dispositivos conectados
          </button>
        </div>
        <div className="user-profile__left-rail-actions">
          <Button
            className="mt-16 mbi-0"
            onClick={() => modalRef.current?.openModal()}
            disabled={sessions.length === 0}
          >
            <ButtonLabel>desconectar todos</ButtonLabel>
          </Button>
        </div>
      </div>

      <div className="user-profile__main">
        <div className="user-profile__main-header">
          <span className="user-profile__main-title">
            Dispositivos conectados
          </span>
        </div>
        <div className="user-profile__main-divider" />

        <div className="up-sessions">
          {sessions.length === 0 && (
            <p className="up-sessions__empty">Nenhum dispositivo conectado.</p>
          )}
          {sessions.map((item) => (
            <div key={item.id} className="up-sessions__card">
              <div className="up-sessions__card-icon">
                <Icon name={convertUserAgentToBrowser(item.userAgent)} />
              </div>
              <div className="up-sessions__card-info">
                <strong className="up-sessions__card-browser">
                  {item.userAgent
                    ? `${convertUserAgentToBrowser(item.userAgent)} — ${convertUserAgentToOS(item.userAgent)}`
                    : 'Não informado'}
                </strong>
                <span className="up-sessions__card-detail">
                  Última sessão:{' '}
                  {item.lastUseAt
                    ? formatDateToView(item.lastUseAt)
                    : 'Não informado'}
                </span>
                <span className="up-sessions__card-detail">
                  IP: {item.ip || 'Não informado'}
                </span>
              </div>
              <div className="up-sessions__card-action">
                {item.id === currentSession?.id ? (
                  <span className="up-sessions__current-badge">
                    Sessão atual
                  </span>
                ) : (
                  <button
                    className="user-profile__icon-btn"
                    onClick={() => removeSession(item.id)}
                    title="Desconectar dispositivo"
                  >
                    <Icon name="trashButton" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Modal ref={modalRef}>
        <div className="caution">
          <Icon name="caution" />
        </div>
        <ModalTitle>desconectar todos os dispositivos</ModalTitle>
        <ModalDescription>
          Todas as sessões ativas da sua Conta SBT serão encerradas. Deseja
          continuar?
        </ModalDescription>
        <div className="modal-actions">
          <Button full className="mbi-0" onClick={removeAllSessions}>
            <ButtonLabel>desconectar todos</ButtonLabel>
          </Button>
          <Button
            full
            customColors="btn-white"
            className="mbi-0"
            onClick={() => modalRef.current?.closeModal()}
          >
            <ButtonLabel>cancelar</ButtonLabel>
          </Button>
        </div>
      </Modal>
    </div>
  );
}
